import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash/fp';
import { mapDevicesToFormProps } from '../../../utils';
import { routePaths } from '../../../../../routePaths';
import { OnSubmit, RecipeLineFormValue, UpdateProductProps } from '../types';
import ProductForm from '../ProductForm';
import { usePriceService } from '../../../../../utils/hooks/priceService';
import { EditFormLayout } from '../../../../../ui-kit';
import { Device } from '../../../../../server-types';
import { LocationReferrer } from '../../../../../types/locationReferrer';
import { PRODUCTS_PER_PAGE } from '../../../constants';
import { getUrlParams } from '../../../../../utils/helpers/urlParams';
import {
    GetPaginatedProductsQueryVariables,
    GetProductComponent,
    refetchGetPaginatedProductsQuery,
    useRemoveProductMutation,
    useUpdateProductAndRecipeMutation,
} from '../../../../../generated/graphql';

const UpdateProductForm = ({ productId }: UpdateProductProps) => {
    const history = useHistory<LocationReferrer>();
    const { state = {} } = history.location;
    const { referrer } = state;
    const params = ['query', 'categories', 'rowsPerPage', 'page'];
    const [queryParam, categoriesParam, rowsPerPageParam, pageParam] = getUrlParams({
        searchString: referrer as string | undefined,
        params,
    });
    const name = queryParam !== null ? queryParam : undefined;
    let selectedCategories: string[] | undefined;
    if (categoriesParam){
        selectedCategories = categoriesParam.split(',');
    }
    const page = parseInt(pageParam || '0', 10);
    const rowsPerPage = parseInt(rowsPerPageParam || PRODUCTS_PER_PAGE.toString(), 10);
    const offset = page * rowsPerPage;

    const refetchVariables: GetPaginatedProductsQueryVariables = {
        pagination: { offset, limit: rowsPerPage },
        name,
        categories: selectedCategories,
    };

    const [updateProduct] = useUpdateProductAndRecipeMutation({
        refetchQueries: [refetchGetPaginatedProductsQuery(refetchVariables)],
    });
    const [remove] = useRemoveProductMutation(
        { refetchQueries: [refetchGetPaginatedProductsQuery(refetchVariables)] }
    );

    const { priceService } = usePriceService();

    const onSubmit: OnSubmit = async values => {
        const { priceSell: rawSell, printingDeviceIds, warehouseCardId, warehouseId, recipeLines, ...rest } = values;
        const priceSell = isNumber(rawSell) ? priceService.toInteger(rawSell) : undefined;

        const mappedRecipeLines = recipeLines?.map(({ absoluteWaste, wastePercentage, stockCard, ...other }) => ({
            ...other,
            waste: absoluteWaste,
        }));

        const recipe = {
            recipeLines: mappedRecipeLines,
        };

        try {
            await updateProduct({
                variables: {
                    id: productId,
                    product: {
                        priceSell,
                        stockCardId: warehouseCardId,
                        stockCardWarehouseId: warehouseId,
                        printingDeviceIds: printingDeviceIds.map(({ id }) => id),
                        ...rest,
                    },
                    recipe,
                },
            });
            history.push(referrer || routePaths.products);
        } catch (error) {
            // nothing we can do
        }
    };

    const onRemove = async () => {
        try {
            await remove({ variables: { id: productId } });
            history.push(routePaths.products);
        } catch (error) {
            // nothing we can do
        }
    };
    const { t } = useTranslation();

    return (
        <GetProductComponent variables={{ id: productId }} redirectOnErrorUrl={routePaths.products} nextFetchPolicy='cache-first'>
            {({ data: { categories, taxes, product, devices, warehouses } }) => {
                if (!product) {
                    return <div />;
                }

                const { connectedStockCard, recipe } = product;

                const initialRecipeLines: Array<RecipeLineFormValue> | undefined = recipe?.recipeLines.map(({
                    id,
                    waste,
                    quantity,
                    stockCard,
                    unitType,
                    warehouseId
                }) => ({
                    id,
                    stockCard,
                    unitType,
                    warehouseId,
                    quantity,
                    stockCardId: stockCard.id,
                    absoluteWaste: waste,
                    wastePercentage: waste,
                }));

                const {
                    taxId,
                    toGoTaxId,
                    categoryId,
                    printingDevices,
                    priceSell,
                    name: displayName,
                    showInPos,
                } = product;
                const initialValues = {
                    name: displayName || '',
                    taxId: taxId || '',
                    printingDeviceIds: printingDevices && printingDevices.length > 0
                        ? mapDevicesToFormProps(printingDevices as Device[]) as Device[]
                        : [],
                    categoryId: categoryId || '',
                    priceSell: priceService
                        .toUnit(priceSell),
                    showInPos,
                    toGoTaxId: toGoTaxId || '',
                    warehouseCardId: connectedStockCard?.stockCardId || '',
                    warehouseId: connectedStockCard?.warehouseId || '',
                    recipeLines: initialRecipeLines,
                };

                return (
                    <EditFormLayout
                        onRemove={onRemove}
                        title={t('Update Product')}
                        buttonText={t('Remove Product')}
                    >
                        <ProductForm
                            connectedWarehouseCard={connectedStockCard?.stockCard}
                            warehouses={warehouses}
                            submitText={t('Update')}
                            onSubmit={onSubmit}
                            devices={devices}
                            categories={categories}
                            taxes={taxes}
                            initialValues={initialValues}
                        />
                    </EditFormLayout>
                );}}
        </GetProductComponent>
    );
};

export default UpdateProductForm;
